import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, AlertTitle, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, Divider, FormControl, IconButton, InputLabel, List, ListItem, ListItemText, MenuItem, Select, Snackbar, TextField } from '@mui/material';
import axios from "axios"
import { Auth } from "aws-amplify";
import { useTranslation } from "react-i18next";

import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';

function EditStudent({ handleClose, studentData }) {
  const studIdRef = useRef();
  const [toastOpen, setToastOpen] = useState(false);
  const [toastType, setToastType] = useState([]);
  const [editForm, setEditForm] = useState({ 
    studentName: "", 
    gender: "",
    // std: "", 
    // divn: "", 
    rollNo: "",
    penNo: "",
    cwsn: ""
  });
  const [editFormError, setEditFormError] = useState({studentName: false, rollNo: false, penNo: false});
  const [editState, setEditState] = useState(false);
  const cwsnLabels = useMemo(() => (
    {
      // 99: "Not Applicable",
      1: "1-Blindness",
      2: "2-Low-Vision",
      3: "3-Hearing impairment",
      4: "4-Speech and Language",
      5: "5-Locomotor Disability",
      6: "6-Mental illness",
      7: "7-Specific Learning Disabilities",
      8: "8-Cerebral palsy",
      9: "9-Autism Spectrum Disorder",
      10: "10-Multiple Disability incl. deaf blindness",
      11: "11-Leprosy Cured students",
      12: "12-Dwarfism",
      13: "13-Intellectual Disability",
      14: "14-Muscular Dystrophy",
      15: "15-Chronic Neurological conditions",
      16: "16-Multiple Sclerosis",
      17: "17-Thalassemia",
      18: "18-Haemophilia",
      19: "19-Sickle Cell disease",
      20: "20-Acid Attack victim",
      21: "21-Parkinson's disease",
    }
  ), [])

  const { t } = useTranslation()
  
  useEffect(() => {
    if(studIdRef.current !== studentData.studId){
      setEditForm({
        studentName: studentData.studentName, 
        gender: studentData.gender,
        // std: studentData.std, 
        // divn: studentData.divn, 
        rollNo: studentData.rollNo,
        penNo: studentData.penNo ? studentData.penNo : "",
        cwsn: studentData.cwsn === "Not Applicable" ? "99" : Object.keys(cwsnLabels).find(key => cwsnLabels[key] === studentData.cwsn)
      })
      setEditFormError({studentName: false, rollNo: false, penNo: false})
      studIdRef.current = studentData.studId
    }
  }, [studentData, cwsnLabels]);

  const handleToastClose = (event, reason) => {
    if (reason === 'clickaway') return
    setToastOpen(false);
  };

  const handleEditFormChange = (field, value) => {
    // console.log(value)
    setEditForm(prev => ({...prev, [field]: value}))
    if(field === "studentName"){
      // (?!\s) to match any whitespace at beginning
      // This matches a-z and A-Z with space but not at starting and also rejects any whitespace character and allows 1 to 64 characters
      if((/^(?!\s)([A-Za-z ]){1,64}$/).test(value.trim())) setEditFormError(prev => ({...prev, [field]: false}))   
      else setEditFormError(prev => ({...prev, [field]: true}))
    }
    if(field === "rollNo"){
      if((/^(?!\s)([1-9]{1})$|^(?!\s)([1-9][0-9]{1})$/).test(value.trim())) setEditFormError(prev => ({...prev, [field]: false}))   
      else setEditFormError(prev => ({...prev, [field]: true}))
    }
    if(field === "penNo"){
      if (/^$|^\d{11,12}$/.test(value.trim())) setEditFormError(prev => ({...prev, [field]: false}))   
      else setEditFormError(prev => ({...prev, [field]: true}))
    }
  }

  const handleEdit = () => {
    if(!studentData.test){
      alert(t("commonItems.error_testNotStarted"))
      return
    }

    const studentName = editForm.studentName.trim()
    const rollNo = String(editForm.rollNo).trim()
    const gender = editForm.gender.trim()
    const penNo = String(editForm.penNo).trim()
    const cwsn = editForm.cwsn

    if(!((/^(?!\s)([A-Za-z ]){1,64}$/).test(studentName) && (/^(?!\s)([1-9]{1})$|^(?!\s)([1-9][0-9]{1})$/).test(rollNo))) {
      setToastType(["warning", t("commonItems.error_requiredFields")])
      setToastOpen(true)
      return
    }
    if(studentData.studentName === studentName && studentData.rollNo === Number(rollNo) && studentData.gender === gender && (studentData.penNo ? studentData.penNo === Number(penNo) : penNo === "") && studentData.cwsn === cwsnLabels[cwsn]) {
      setToastType(["warning", t("admin.students.editStudentDialogue.error_changeField")])
      setToastOpen(true)
      return
    }
    if(!(/^$|^\d{11,12}$/.test(penNo.trim()))) {
      setToastType(["warning", t("admin.students.editStudentDialogue.error_invalidPenNo")])
      setToastOpen(true)
      return
    }
    setEditState(true)

    Auth.currentSession()
    .then(async (data) => {
      const jwtToken = data.idToken.jwtToken;
      const adminId = data.idToken.payload["custom:db_id"]

      try {
        const editResponse =  await axios.put("https://zk1128nq2f.execute-api.ap-south-1.amazonaws.com/alpha/admin/"+adminId+"/students", 
          JSON.stringify({
            "studId": studentData.studId,
            "studentName": studentName,
            "gender": gender,
            "rollNo": rollNo,
            // "penNo": penNo,
            "cwsn": cwsn
          }),
          {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': jwtToken
          }
        });
        // console.log("Edit Response: ",editResponse.data)
        if(editResponse.data.message === "Edit Successful!") {
          alert(t("admin.students.editStudentDialogue.info_editSuccess"));
          setToastType(["success", "Edit Student Entry Successful!"])
          window.location.reload()
        }
        else{
          alert("Edit Cancelled: \n"+editResponse.data.message)
          setToastType(["error", editResponse.data.message])
        }
        setToastOpen(true)
        setEditState(false)
      }
      catch (error) {
        if ([404, 406, 422].includes(error.response.status)) setToastType(["error", error.response.data.message])
        else{
          console.log(error)
          setToastType(["error", t("commonItems.error_api_generic")])
        }
        setToastOpen(true)
        setEditState(false)
      }
    })
    .catch((err) => console.log(err));
  }

  return (
    <>
      <Snackbar open={toastOpen} autoHideDuration={5000} onClose={handleToastClose}>
        <Alert onClose={handleToastClose} severity={toastType[0]} sx={{ width: '100%' }}>
        {toastType[1]}
        </Alert>
      </Snackbar>
      <DialogTitle sx={{display: "flex", alignItems: "center", gap: "0.5rem"}}>
        <EditIcon color="info" sx={{fontSize: "1.25rem"}}/>
        <b>{t("admin.students.editStudentDialogue.title")}</b>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8
        }}
      >
        <CloseIcon />
      </IconButton>
      {false ? 
        <>
          <DialogContent>
            <Alert severity="warning" className="uploadInstructions">
              <AlertTitle><b>{t("commonItems.info_featureNotAvailable")}</b></AlertTitle>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button color="info" variant="contained" onClick={handleClose}>{t("commonItems.buttonLabel_close")}</Button>
          </DialogActions>
        </>
      :
        <>
          {(studentData.test && studentData.test.superAdminId===24) ?
            <>
              <DialogContent>
                <Alert severity="info">
                  <AlertTitle><b>{t("admin.students.editStudentDialogue.info_KVStudentEdit")}</b></AlertTitle>
                </Alert>
              </DialogContent>
              <DialogActions>
                <Button color="info" variant="contained" onClick={handleClose}>{t("commonItems.buttonLabel_close")}</Button>
              </DialogActions>
            </>
          :
            <>
              <DialogContent dividers>
                <div className='editStudent' style={{display: "flex", flexDirection: "column", gap: "1rem"}}>
                  {(studentData.test ? studentData.test.status===0 : false) ?
                    <>
                      <div className="editStudentDetails" style={{display: "flex", gap:"1rem", backgroundColor: "#F1F3F9", borderRadius: "10px", padding: "1rem"}}>
                        <div style={{display: "flex", flexDirection: "column", gap: "0.5rem"}}>
                          <h4>{t("commonItems.textLabel_studentBasicDetails")}:</h4>
                          <p style={{marginBottom: "0.5rem"}}>{t("commonItems.textLabel_orgStudId")}: <span style={{fontWeight: 600}}>{studentData.orgStudId}</span></p>
                          {/* <div style={{display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "center", marginBottom: "0.5rem"}}>
                            <FormControl sx={{ minWidth: 150}} size="small">
                              <TextField
                                error = {editFormError.penNo} 
                                helperText = {editFormError.penNo ? "Pen No must be 11 or 12 digits." : false} 
                                // margin="dense"
                                id="penNo"
                                name="PEN No"
                                label="Pen No"
                                // fullWidth
                                variant="outlined"
                                size="small"
                                value={editForm.penNo}
                                onChange={(e) => handleEditFormChange("penNo", e.target.value)}
                              />
                            </FormControl>
                            <p style={{fontSize: "14px", color: "gray"}}>If Pen No not generated leave the field blank.</p>
                          </div> */}
                          <div style={{display: "flex", flexWrap: "wrap", gap: "1rem"}}>
                            <FormControl sx={{ minWidth: 150}} size="small">
                              <TextField
                                error = {editFormError.studentName} 
                                helperText = {editFormError.studentName ? t("admin.students.editStudentDialogue.error_invalidStudentName") : false} 
                                required
                                // margin="dense"
                                id="studentName"
                                name={t("commonItems.textfieldLabel_studentName")}
                                label={t("commonItems.textfieldLabel_studentName")}
                                // fullWidth
                                variant="outlined"
                                size="small"
                                value={editForm.studentName}
                                onChange={(e) => handleEditFormChange("studentName", e.target.value)}
                              />
                            </FormControl>
                            <FormControl sx={{minWidth: 150}} size="small">
                              <InputLabel id="genderFilterLabel">{t("commonItems.dropdownLabel_gender")}</InputLabel>
                              <Select
                                labelId="genderFilterLabel"
                                id="genderFilter"
                                value={editForm.gender}
                                onChange={(e) => handleEditFormChange("gender", e.target.value)}
                                autoWidth
                                label="Gender"
                              >
                                {/* <MenuItem value="">
                                  <em>None</em>
                                </MenuItem> */}
                                <MenuItem value="M">Male</MenuItem>
                                <MenuItem value="F">Female</MenuItem>
                              </Select>
                            </FormControl>
                            <FormControl sx={{minWidth: 150}} size="small">
                              <InputLabel id="cwsnFilterLabel">{t("commonItems.dropdownLabel_cwsnCategory")}</InputLabel>
                              <Select
                                labelId="cwsnFilterLabel"
                                id="cwsnFilter"
                                value={editForm.cwsn}
                                onChange={(e) => handleEditFormChange("cwsn", e.target.value)}
                                autoWidth
                                label="CWSN Category"
                              >
                                <MenuItem value="99">Not Applicable</MenuItem>
                                {Object.keys(cwsnLabels).map((item, i) => {
                                    return <MenuItem key={i} value={item}>{cwsnLabels[item]}</MenuItem>
                                  })                                 
                                }
                              </Select>
                            </FormControl>
                          </div>
                        </div>
                        <Divider orientation="vertical" flexItem/>
                        <div style={{display: "flex", flexDirection: "column", gap: "0.5rem"}}>
                          <h4>{t("commonItems.textLabel_classDetails")}:</h4>
                          <div style={{display: "flex", flexWrap: "wrap", gap: "1rem", marginBottom: "0.5rem"}}>
                            <p>{t("commonItems.textLabel_class")}: <span style={{fontWeight: 600}}>{studentData.std}</span></p>
                            <p>{t("commonItems.textLabel_section")}: <span style={{fontWeight: 600}}>{studentData.divn}</span></p>
                          </div>
                          <div style={{display: "flex", flexWrap: "wrap", gap: "1rem"}}>
                            {/* <FormControl sx={{minWidth: 150}} size="small">
                              <InputLabel id="stdFilterLabel">Class</InputLabel>
                              <Select
                                labelId="stdFilterLabel"
                                id="stdFilter"
                                value={editForm.std}
                                onChange={(e) => handleEditFormChange("std", e.target.value)}
                                autoWidth
                                label="Class"
                              >
                                <MenuItem value="3">3</MenuItem>
                                <MenuItem value="4">4</MenuItem>
                                <MenuItem value="5">5</MenuItem>
                                <MenuItem value="6">6</MenuItem>
                                <MenuItem value="7">7</MenuItem>
                                <MenuItem value="8">8</MenuItem>
                              </Select>
                            </FormControl>
                            <FormControl sx={{ minWidth: 150}} size="small">
                              <InputLabel id="divnFilterLabel">Section</InputLabel>
                              <Select
                                labelId="divnFilterLabel"
                                id="divnFilter"
                                value={editForm.divn}
                                onChange={(e) => handleEditFormChange("divn", e.target.value)}
                                autoWidth
                                label="Section"
                              >
                                <MenuItem value="A">A</MenuItem>
                                <MenuItem value="B">B</MenuItem>
                                <MenuItem value="C">C</MenuItem>
                                <MenuItem value="D">D</MenuItem>
                                <MenuItem value="E">E</MenuItem>
                                <MenuItem value="F">F</MenuItem>
                                <MenuItem value="G">G</MenuItem>
                                <MenuItem value="H">H</MenuItem>
                                <MenuItem value="I">I</MenuItem>
                              </Select>
                            </FormControl> */}
                            <FormControl sx={{ minWidth: 150}} size="small">
                              <TextField
                                error = {editFormError.rollNo} 
                                helperText = {editFormError.rollNo ? t("admin.students.editStudentDialogue.error_invalidRollNo") : false} 
                                required
                                // margin="dense"
                                id="rollNo"
                                name={t("commonItems.textfieldLabel_rollNo")}
                                label={t("commonItems.textfieldLabel_rollNo")}
                                // fullWidth
                                variant="outlined"
                                size="small"
                                value={editForm.rollNo}
                                onChange={(e) => handleEditFormChange("rollNo", e.target.value)}
                              />
                            </FormControl>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h4 style={{paddingBottom: "0.5rem"}}>{t("admin.students.editStudentDialogue.instructions_title")}</h4>
                        <List dense disablePadding sx={{listStyleType: "decimal", listStylePosition: "inside"}}>
                          <ListItem disableGutters>
                            <ListItemText primary={<p>{t("admin.students.editStudentDialogue.instructions_line1")}</p>}/>
                          </ListItem>
                          <ListItem disableGutters>
                            <ListItemText primary={<p>{t("admin.students.editStudentDialogue.instructions_line2")}</p>}/>
                          </ListItem>
                        </List>
                      </div>
                    </>
                  :
                    <Alert severity="error">
                      <AlertTitle><b>{t("admin.students.editStudentDialogue.error_editNotAllowed")}</b></AlertTitle>
                    </Alert>
                  }
                </div>
              </DialogContent>
              <DialogActions>
                {(studentData.test ? studentData.test.status===0 : false) ? 
                  <>
                    <h4 style={{marginRight: "auto", paddingLeft: "1rem", fontSize: "14px"}}>{t("admin.students.editStudentDialogue.footer")}</h4>
                    <Button color="info" variant="contained" onClick={handleClose}>{t("admin.students.editStudentDialogue.buttonLabel_cancel")}</Button>
                    {editState 
                      ?
                      <Button color="warning" variant="contained" disabled>{t("admin.students.editStudentDialogue.buttonLabel_edit")}<CircularProgress size="1rem" color="inherit" sx={{marginLeft: "0.25rem"}}/></Button>
                      :
                      <Button color="warning" variant="contained" onClick={handleEdit}>{t("admin.students.editStudentDialogue.buttonLabel_edit")}</Button>
                    }
                  </>
                :
                  <Button color="info" variant="contained" onClick={handleClose}>{t("commonItems.buttonLabel_close")}</Button>
                }
              </DialogActions>
            </>
          }
        </>
      }
    </>
  )
}

export default EditStudent